import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Clock3 } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import { trackPurchase } from "@/lib/analytics";

export default function CheckoutSuccess() {
    const [state, setState] = useState("pending");
    const sessionId = new URLSearchParams(window.location.search).get("session_id");

    useEffect(() => {
        if (!sessionId) {
            setState("error");
            return;
        }
        let tries = 0;
        let stop = false;
        const tick = async () => {
            try {
                const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setState("paid");
                    trackPurchase({ transactionId: sessionId, value: data.amount, pack: data.pack_label });
                    return;
                }
            } catch (e) {}
            tries += 1;
            if (tries < 20 && !stop) setTimeout(tick, 2000);
            else setState((s) => (s === "pending" ? "timeout" : s));
        };
        tick();
        return () => {
            stop = true;
        };
    }, [sessionId]);

    const badge =
        state === "paid"
            ? { icon: CheckCircle2, cls: "bg-sage-soft text-sage", label: "Paiement confirmé" }
            : state === "pending"
            ? { icon: Loader2, cls: "bg-terra-soft text-terra", label: "Confirmation en cours…" }
            : { icon: Clock3, cls: "bg-sand/60 text-ink/70", label: "Vérification en cours — e-mail de confirmation à suivre" };

    return (
        <div className="min-h-screen bg-cream grain grid place-items-center px-4" data-testid="success-page">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="max-w-lg w-full rounded-[2rem] bg-white border border-sage/15 shadow-2xl shadow-sage/10 p-8 sm:p-12 text-center"
            >
                <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.2 }}
                    className="mx-auto w-20 h-20 rounded-full bg-sage text-cream grid place-items-center"
                >
                    <CheckCircle2 size={36} strokeWidth={1.6} />
                </motion.span>
                <h1 className="mt-8 font-serif text-3xl sm:text-4xl text-ink">Merci pour votre commande !</h1>
                <p className="mt-4 text-base text-[#4A5550] leading-relaxed">
                    Votre paiement a bien été reçu. Votre commande est préparée
                    avec soin — livraison suivie estimée sous 8 à 12 jours,
                    numéro de suivi dès l’expédition.
                </p>
                <span
                    data-testid="success-status-badge"
                    className={`mt-8 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${badge.cls}`}
                >
                    <badge.icon size={16} className={state === "pending" ? "animate-spin" : ""} />
                    {badge.label}
                </span>
                <div className="mt-10">
                    <Link
                        data-testid="success-back-button"
                        to="/"
                        className="inline-flex rounded-full bg-sage hover:bg-sage-hover text-cream font-semibold px-8 py-4 transition-all duration-300 hover:scale-[1.02]"
                    >
                        Retour à la boutique
                    </Link>
                </div>
            </motion.div>
        </div>
    );
}
